import * as CSS from "csstype";
import { Button, Label, TextField } from "@metricell/smarttoolscore";
import {
    INotificationProviderRef,
    Loading,
    NotificationProvider,
} from "./Components";
import React, { useEffect, useRef, useState } from "react";
import { StringUtil, Utils } from "../Utilities";
import { IReturnModel } from ".";
import "@metricell/smarttoolscore/dist/module/Styles/metricell.css";

interface ISideNavOptions {
    id: number;
    imgURL: string;
    label: string;
}

interface IEditPasskey {
    passkeyId: string;
    userName: string;
    displayName: string;
    authenticatorName: string;
    createUTCDateTime: string;
    lastLoginAttemptUTCDateTime: string;
}

export const EditPasskeysRoot: React.FunctionComponent =
    (): React.ReactElement => {
        const notificationProviderRef = useRef<INotificationProviderRef>(),
            [loading, setLoading] = useState<boolean>(false),
            [selectedSection, setSelectedSection] = useState<number>(0),
            [userPasskeys, setUserPasskeys] = useState<IEditPasskey[]>(null),
            returnURL =
                window.location.origin +
                new URLSearchParams(window.location.search)
                    .get(`ReturnURL`)
                    .replace(`%2f`, `/`),
            sideNavOptions: ISideNavOptions[] = [
                {
                    id: 0,
                    imgURL: `/Images/Icon?icon=230100&folder=Module`,
                    label: `My Passkeys`,
                },
                {
                    id: 1,
                    imgURL: `/Images/LogoutSectionIcon.svg`,
                    label: `Logout`,
                },
            ];

        useEffect(() => {
            void getMyPasskeys();
        }, []);

        const getMyPasskeys = async (): Promise<void> => {
            setLoading(true);

            const stringResult: string = await $.ajax({
                method: `GET`,
                url: `/Passkey/GetMyPasskeys`,
            });

            const result: IReturnModel<IEditPasskey[]> =
                JSON.parse(stringResult);

            if (result.isSuccess) {
                setUserPasskeys(result.value);
            }
            setLoading(false);
        };

        const sideMenuClickEvent = (key: number): void => {
            if (key === 1) {
                location.href = `/Account/Logout`;
            } else {
                setSelectedSection(key);
            }
        };

        const handleDelete = async (i: IEditPasskey): Promise<void> => {
            await $.ajax({
                data: {
                    passkeyId: i.passkeyId,
                    userName: i.userName,
                    displayName: i.displayName,
                    createUTCDateTime: i.createUTCDateTime,
                },
                error: (error) => {
                    notificationProviderRef.current.addNotificationToQueue({
                        message: `An error has occurred.`,
                        severity: `error`,
                    });
                },
                method: `POST`,
                success: () => {
                    const updatedData = userPasskeys.filter(
                        (item) => item.passkeyId !== i.passkeyId,
                    );
                    setUserPasskeys(updatedData);
                    notificationProviderRef.current.addNotificationToQueue({
                        message: `Changes have been saved.`,
                        severity: `info`,
                    });
                },
                url: `/Passkey/DeleteMyPasskey`,
            });
        };

        const renderMyPasskeys = (): JSX.Element => {
            return (
                <div className={`editprofile-container`}>
                    <div className={`editpasskey-section1`}>
                        <div className={`greybackground rounded`}>
                            <table>
                                <thead>
                                    <tr>
                                        <th>Authenticator Name</th>
                                        <th>Date created</th>
                                        <th>Last Login date</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {userPasskeys &&
                                        userPasskeys.map((i) => (
                                            <tr key={i.passkeyId}>
                                                <td>{i.authenticatorName}</td>
                                                <td>
                                                    {i.createUTCDateTime !==
                                                    null
                                                        ? new Date(
                                                              i.createUTCDateTime,
                                                          )
                                                              .toLocaleString(
                                                                  undefined,
                                                                  {
                                                                      day: "2-digit",
                                                                      month: "2-digit",
                                                                      year: "numeric",
                                                                      hour: "2-digit",
                                                                      minute: "2-digit",
                                                                      hour12: false,
                                                                  },
                                                              )
                                                              .replace(",", "")
                                                        : ""}
                                                </td>
                                                <td>
                                                    {i.lastLoginAttemptUTCDateTime !==
                                                    null
                                                        ? new Date(
                                                              i.lastLoginAttemptUTCDateTime,
                                                          )
                                                              .toLocaleString(
                                                                  undefined,
                                                                  {
                                                                      day: "2-digit",
                                                                      month: "2-digit",
                                                                      year: "numeric",
                                                                      hour: "2-digit",
                                                                      minute: "2-digit",
                                                                      hour12: false,
                                                                  },
                                                              )
                                                              .replace(",", "")
                                                        : ""}
                                                </td>
                                                <td>
                                                    <button
                                                        onClick={() =>
                                                            handleDelete(i)
                                                        }
                                                    >
                                                        Delete
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            );
        };

        if (loading) {
            return <Loading />;
        } else {
            return (
                <div className={`editprofile-rootcontainer`}>
                    <NotificationProvider ref={notificationProviderRef} />
                    <div
                        className={`editprofile-sidepanel greybackground rounded`}
                    >
                        <div className={`editprofile-title`}>
                            <Label typography={`heading_01`}>Passkeys</Label>
                        </div>
                        <div className={`subsection-toolcontainer`}>
                            {sideNavOptions.map((option) => (
                                <div
                                    className={`${
                                        selectedSection === option.id
                                            ? `toolcontainer-selectedsection `
                                            : ``
                                    } toolcontainer-tool`}
                                    key={option.id}
                                    onClick={(): void =>
                                        sideMenuClickEvent(option.id)
                                    }
                                >
                                    <img src={option.imgURL} />
                                    <div className={`tool-text`}>
                                        <Label typography={`body`}>
                                            {option.label}
                                        </Label>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div
                            className={`editprofile-returnbuttoncontainer editprofile-button-margin`}
                        >
                            <Button href={returnURL} type={`primary`}>
                                Return
                            </Button>
                        </div>
                    </div>
                    <div className={`editprofile-mainbody`}>
                        <div className={`greybackground rounded`}>
                            <div className={`editprofile-title`}>
                                <Label typography={`heading_01`}>
                                    {sideNavOptions[selectedSection].label}
                                </Label>
                            </div>
                        </div>
                        {selectedSection === 0 && renderMyPasskeys()}
                    </div>
                </div>
            );
        }
    };
